import { useQuery } from "@tanstack/react-query";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Cell,
} from "recharts";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";

interface SearchMetricsResponse {
  length_distribution: Array<{ bucket: string; count: number }>;
}

const BUCKET_COLORS: Record<string, string> = {
  "01_<500자 (단일메시지)": "#10b981",
  "02_500~1700자 (단일메시지)": "#3b82f6",
  "03_1700~3400자 (2개 분할)": "#f59e0b",
  "04_3400~5100자 (3개 분할)": "#f97316",
  "05_5100~6800자 (4개 분할)": "#ef4444",
  "06_6800자+ (5+개 분할)": "#dc2626",
};

/** v1.4 답변 길이 분포 차트 (최근 7일) */
export function AnswerLengthChart() {
  const { data, isLoading } = useQuery<SearchMetricsResponse>({
    queryKey: ["admin", "search-metrics"],
    queryFn: () => api.get("/admin/search-metrics"),
    refetchInterval: 60_000,
  });

  if (isLoading || !data) {
    return <Skeleton className="h-64" />;
  }

  const chartData = data.length_distribution.map((d) => ({
    bucket: d.bucket.replace(/^\d+_/, ""),  // 정렬용 prefix 제거
    bucketKey: d.bucket,
    count: d.count,
  }));

  const total = chartData.reduce((s, d) => s + d.count, 0);
  const singleMsg = chartData
    .filter((d) => d.bucketKey.startsWith("01") || d.bucketKey.startsWith("02"))
    .reduce((s, d) => s + d.count, 0);
  const singleRate = total > 0 ? Math.round((singleMsg / total) * 100) : 0;

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold">답변 길이 분포 (최근 7일)</h3>
          <div className="text-xs text-gray-500">
            단일 메시지 비율 <span className="font-semibold text-green-600">{singleRate}%</span>
          </div>
        </div>

        {chartData.length === 0 ? (
          <div className="h-48 flex items-center justify-center text-sm text-gray-400">
            데이터 없음
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={chartData} margin={{ left: -10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="bucket" fontSize={10} angle={-15} textAnchor="end" height={60} />
              <YAxis fontSize={11} />
              <Tooltip />
              <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                {chartData.map((entry, idx) => (
                  <Cell key={idx} fill={BUCKET_COLORS[entry.bucketKey] || "#3b82f6"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}

        <div className="mt-2 text-xs text-gray-500">
          1,700자 이상은 NW Bot에서 여러 메시지로 분할 전송됩니다.
        </div>
      </CardContent>
    </Card>
  );
}
